import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  CircleDot,
  Copy,
  ExternalLink,
  Github,
  KeyRound,
  Loader2,
  Plane,
  ServerCog,
  Waypoints,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  useBosunProject,
  useSubmitBosunCodexSetup,
  useSubmitBosunSlackAppToken,
  useDeleteBosunProject,
  useJob,
  useWorkspace,
  type BosunProjectRead,
} from "@/api/queries";
import ErrorFallback from "@/components/ErrorFallback";
import { ProjectCockpitNav } from "@/components/ProjectCockpitNav";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { formatError } from "@/lib/format-error";
import { canManageMembers } from "@/api/workspace-permissions";

export const Route = createFileRoute(
  "/_authenticated/projects/$workspaceUuid/$projectUuid/provisioning",
)({
  component: ProjectProvisioningPage,
  errorComponent: ErrorFallback,
});

type StepState = "pending" | "waiting" | "running" | "completed" | "failed";
type ProviderStep = BosunProjectRead["provider_steps"][number];

const CLIENT_SECRET_NAMES = [
  "BOSUN_DOT_ENV",
  "DATABASE_URL_DIRECT",
  "DATABASE_URL",
  "R2_SECRET_ACCESS_KEY",
  "R2_ACCESS_KEY_ID",
  "R2_ACCOUNT_ID",
  "R2_BUCKET_NAME",
  "R2_ENDPOINT",
] as const;
const HIDDEN_STEP_LINK_KEYS = new Set(["release_notes_surface"]);

const CLIENT_SECRET_NAME_PATTERN = new RegExp(
  `\\b(${CLIENT_SECRET_NAMES.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`,
  "g",
);
const CLIENT_SECRET_ASSIGNMENT_PATTERN = new RegExp(
  `\\b(${CLIENT_SECRET_NAMES.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\s*=\\s*("[^"]*"|'[^']*'|\\S+)`,
  "g",
);

function sanitizeClientProvisioningText(value: string): string {
  return value
    .replace(CLIENT_SECRET_ASSIGNMENT_PATTERN, "runtime secret=[redacted]")
    .replace(/\/codex\/login\b/g, "[removed generated-Hermes login path]")
    .replace(CLIENT_SECRET_NAME_PATTERN, "runtime secret");
}

const STEP_DEFS = [
  {
    key: "credentials",
    label: "Credential validation",
    icon: KeyRound,
    sourceKeys: ["github_secret", "slack_app", "slack_channels"],
  },
  {
    key: "repo",
    label: "Repo setup",
    icon: Github,
    sourceKeys: [
      "github_repo",
      "hermes_config",
      "kanban_board",
      "release_notes_surface",
    ],
  },
  {
    key: "fly",
    label: "Fly deploy",
    icon: Plane,
    sourceKeys: ["fly_neon_cloudflare"],
  },
  {
    key: "hermes",
    label: "Hermes startup",
    icon: ServerCog,
    sourceKeys: ["generated_hermes"],
  },
  {
    key: "readiness",
    label: "Bootstrap readiness",
    icon: Waypoints,
    sourceKeys: ["workspace_codex_login", "codex_handoff", "bootstrap_pm_ticket"],
  },
] as const;

function normalizeStatus(
  status: string | undefined,
  options: { hasError?: boolean } = {},
): StepState {
  if (options.hasError) return "failed";
  if (
    status === "completed" ||
    status === "ready" ||
    status === "stubbed" ||
    status === "succeeded"
  )
    return "completed";
  if (status === "failed") return "failed";
  if (status === "blocked" || status === "manual_pending" || status === "approval_required")
    return "waiting";
  if (status === "in_progress" || status === "running") return "running";
  return "pending";
}

function aggregateStatus(
  project: BosunProjectRead,
  sourceKeys: readonly string[],
): StepState {
  const source = project.provider_steps.filter((step) =>
    sourceKeys.includes(step.key),
  );
  if (source.length === 0)
    return project.status === "ready" ? "completed" : "pending";
  const statuses = source.map((step) =>
    normalizeStatus(step.status, { hasError: Boolean(step.error) }),
  );
  if (statuses.includes("failed")) return "failed";
  if (statuses.includes("running")) return "running";
  if (statuses.includes("waiting")) return "waiting";
  if (statuses.every((status) => status === "completed")) return "completed";
  if (
    project.status === "running" &&
    statuses.some((status) => status === "pending")
  )
    return "running";
  return "pending";
}

function statusLabel(status: StepState): string {
  if (status === "failed") return "action required";
  return status;
}

function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return null;
  }
  const wholeSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(wholeSeconds / 60);
  const remainder = wholeSeconds % 60;
  if (minutes === 0) return `${remainder}s`;
  if (remainder === 0) return `${minutes}m`;
  return `${minutes}m ${remainder}s`;
}

function timingCopy(step: ProviderStep, status: StepState): string | null {
  if (step.is_manual) return "Waiting for manual setup";
  if (status === "completed") {
    const duration = formatDuration(step.duration_seconds);
    return duration && (step.duration_seconds ?? 0) > 0
      ? `Completed in ${duration}`
      : step.estimated_seconds
        ? `Completed; expected ${formatDuration(step.estimated_seconds)}`
        : "Completed";
  }
  if (status === "running") {
    const elapsed = formatDuration(step.elapsed_seconds);
    const remaining = formatDuration(step.remaining_seconds);
    if (elapsed && remaining) return `${elapsed} elapsed, about ${remaining} remaining`;
    if (remaining) return `About ${remaining} remaining`;
    return step.estimated_seconds
      ? `Expected ${formatDuration(step.estimated_seconds)}`
      : null;
  }
  if (status === "pending" && step.estimated_seconds) {
    return `Expected ${formatDuration(step.estimated_seconds)}`;
  }
  if (status === "waiting") return step.progress_label ?? "Waiting for prerequisites";
  return null;
}

function phaseTimingCopy(sourceSteps: ProviderStep[], status: StepState): string | null {
  const automated = sourceSteps.filter((step) => !step.is_manual);
  if (automated.length === 0) {
    return status === "waiting" ? "Waiting for manual setup" : null;
  }
  const elapsed = automated.reduce((total, step) => total + (step.elapsed_seconds ?? 0), 0);
  const remaining = automated.reduce((total, step) => total + (step.remaining_seconds ?? 0), 0);
  const estimated = automated.reduce((total, step) => total + (step.estimated_seconds ?? 0), 0);
  const completed = automated.reduce((total, step) => total + (step.duration_seconds ?? 0), 0);
  if (status === "completed" && completed > 0) {
    return `Completed in ${formatDuration(completed)}`;
  }
  if (status === "completed") {
    return estimated > 0 ? `Completed; expected ${formatDuration(estimated)}` : "Completed";
  }
  if (status === "running" && (elapsed > 0 || remaining > 0)) {
    const elapsedText = formatDuration(elapsed);
    const remainingText = formatDuration(remaining);
    if (elapsedText && remainingText) return `${elapsedText} elapsed, about ${remainingText} remaining`;
  }
  if ((status === "pending" || status === "running") && estimated > 0) {
    return `Expected ${formatDuration(estimated)}`;
  }
  if (status === "waiting") return "Waiting for prerequisites";
  return null;
}

function phaseProgressPercent(sourceSteps: ProviderStep[], status: StepState): number | null {
  const automated = sourceSteps.filter(
    (step) => !step.is_manual && typeof step.progress_percent === "number",
  );
  if (automated.length === 0) return null;
  if (status === "completed") return 100;
  const total = automated.reduce((sum, step) => sum + (step.progress_percent ?? 0), 0);
  return Math.max(0, Math.min(100, Math.round(total / automated.length)));
}

function toneForStatus(
  status: StepState,
): React.ComponentProps<typeof Badge>["tone"] {
  if (status === "completed") return "success";
  if (status === "failed") return "danger";
  if (status === "waiting") return "warning";
  if (status === "running") return "info";
  return "neutral";
}

function iconForStatus(status: StepState) {
  if (status === "completed")
    return <CheckCircle2 className="size-4 text-success" />;
  if (status === "failed")
    return <AlertTriangle className="size-4 text-destructive" />;
  if (status === "waiting")
    return <CircleDot className="size-4 text-warning" />;
  if (status === "running")
    return <Loader2 className="size-4 animate-spin text-primary" />;
  return <CircleDot className="size-4 text-muted-foreground" />;
}

function ProgressBar({ value }: { value: number }) {
  return (
    <div
      className="h-2 overflow-hidden rounded bg-white/10"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
    >
      <div
        className="h-full rounded bg-primary transition-[width]"
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </div>
  );
}

function renderScalar(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return sanitizeClientProvisioningText(String(value));
  }
  if (Array.isArray(value))
    return value
      .map((item) => renderScalar(item))
      .filter(Boolean)
      .join(", ");
  return null;
}

function renderStepText(value: string | null | undefined): string | null {
  if (!value) return null;
  return sanitizeClientProvisioningText(value);
}

function stepFacts(step: ProviderStep): Array<[string, string]> {
  const resources = step.resources ?? {};
  const facts: Array<[string, string]> = [];
  const githubActions = resources.github_actions;
  if (
    githubActions &&
    typeof githubActions === "object" &&
    !Array.isArray(githubActions)
  ) {
    const data = githubActions as Record<string, unknown>;
    for (const [label, key] of [
      ["Workflow", "workflow_id"],
      ["Run", "run_id"],
      ["Ref", "ref"],
      ["SHA", "sha"],
      ["Workflow URL", "workflow_url"],
      ["Logs", "logs_url"],
      ["Actions status", "run_status"],
      ["Conclusion", "run_conclusion"],
    ] as const) {
      const rendered = renderScalar(data[key]);
      if (rendered) facts.push([label, rendered]);
    }
    const failedJob = data.failed_job;
    if (failedJob && typeof failedJob === "object" && !Array.isArray(failedJob)) {
      const job = failedJob as Record<string, unknown>;
      const name = renderScalar(job.name);
      const conclusion = renderScalar(job.conclusion);
      const htmlUrl = renderScalar(job.html_url);
      if (name || conclusion) facts.push(["Failed job", [name, conclusion].filter(Boolean).join(" - ")]);
      if (htmlUrl) facts.push(["Failed job URL", htmlUrl]);
    }
    const failedSteps = data.failed_steps;
    if (Array.isArray(failedSteps)) {
      const rendered = failedSteps
        .map((step) => {
          if (!step || typeof step !== "object" || Array.isArray(step)) return null;
          const record = step as Record<string, unknown>;
          const name = renderScalar(record.name);
          const conclusion = renderScalar(record.conclusion);
          return [name, conclusion].filter(Boolean).join(" - ");
        })
        .filter(Boolean)
        .join(", ");
      if (rendered) facts.push(["Failed steps", rendered]);
    }
  }
  const deployment = resources.deployment;
  if (
    deployment &&
    typeof deployment === "object" &&
    !Array.isArray(deployment)
  ) {
    const data = deployment as Record<string, unknown>;
    for (const [label, key] of [
      ["Fly app", "app"],
      ["Machine count", "machine_count"],
      ["Running machines", "running_machine_count"],
      ["Image machines", "image_machine_count"],
      ["Health", "verification_status"],
      ["Failure class", "failure_class"],
      ["Failure reason", "failure_reason"],
      ["Attempts", "verification_attempts"],
      ["Machine states", "states"],
    ] as const) {
      const rendered = renderScalar(data[key]);
      if (rendered) facts.push([label, rendered]);
    }
  }
  const workflowGate = resources.workflow_gate;
  if (
    workflowGate &&
    typeof workflowGate === "object" &&
    !Array.isArray(workflowGate)
  ) {
    const data = workflowGate as Record<string, unknown>;
    for (const [label, key] of [
      ["Workflow gate", "status"],
      ["Gate phase", "phase"],
      ["Suppressed until", "suppressed_until"],
      ["Safe to enable next", "safe_to_enable_next"],
    ] as const) {
      const rendered = renderScalar(data[key]);
      if (rendered) facts.push([label, rendered]);
    }
    const decisions = data.decisions;
    if (Array.isArray(decisions)) {
      const rendered = decisions
        .map((decision) => {
          if (
            !decision ||
            typeof decision !== "object" ||
            Array.isArray(decision)
          )
            return null;
          const record = decision as Record<string, unknown>;
          const workflow = renderScalar(record.workflow_id);
          const state = renderScalar(record.state);
          return workflow && state
            ? `${workflow}: ${state}`
            : (workflow ?? state);
        })
        .filter(Boolean)
        .join(", ");
      if (rendered) facts.push(["Workflow decisions", rendered]);
    }
    const postStagedChecks = data.post_staged_checks;
    if (
      postStagedChecks &&
      typeof postStagedChecks === "object" &&
      !Array.isArray(postStagedChecks)
    ) {
      const rendered = renderScalar(
        (postStagedChecks as Record<string, unknown>).status,
      );
      if (rendered) facts.push(["Post-staged suite", rendered]);
    }
  }
  for (const [label, key] of [
    ["Fly app", "fly_app"],
    ["URL", "url"],
    ["Source blocker", "missing_slack_api_capability"],
    ["Next step", "next_step"],
  ] as const) {
    const rendered = renderScalar(resources[key]);
    if (rendered) facts.push([label, rendered]);
  }
  const slackApproval = resources.slack_approval;
  if (
    slackApproval &&
    typeof slackApproval === "object" &&
    !Array.isArray(slackApproval)
  ) {
    const data = slackApproval as Record<string, unknown>;
    for (const [label, key] of [
      ["Workspace", "workspace_uuid"],
      ["Project", "project_uuid"],
      ["Project slug", "project_slug"],
      ["Slack app", "slack_app_id"],
      ["Provider step", "provider_step"],
      ["Provider status", "provider_status"],
      ["Approval status", "approval_status"],
      ["Retry guidance", "retry_guidance"],
    ] as const) {
      const rendered = renderScalar(data[key]);
      if (rendered) facts.push([label, rendered]);
    }
  }
  const slackAppTokenHandoff = resources.slack_app_token_handoff;
  if (
    slackAppTokenHandoff &&
    typeof slackAppTokenHandoff === "object" &&
    !Array.isArray(slackAppTokenHandoff)
  ) {
    const data = slackAppTokenHandoff as Record<string, unknown>;
    for (const [label, key] of [
      ["Slack app", "slack_app_id"],
      ["Settings", "settings_url"],
      ["Token prefix", "token_prefix"],
      ["Validation", "validation_method"],
    ] as const) {
      const rendered = renderScalar(data[key]);
      if (rendered) facts.push([label, rendered]);
    }
  }
  return facts;
}

function slackAppSettingsUrl(slackAppId: string | undefined): string | null {
  if (!slackAppId || !/^A[A-Z0-9]+$/i.test(slackAppId)) return null;
  return `https://api.slack.com/apps/${encodeURIComponent(slackAppId)}/general`;
}

function deletionResourceRows(project: BosunProjectRead): Array<[string, string, string]> {
  const resources = project.deletion_status_payload?.resources;
  if (!Array.isArray(resources)) return [];
  return resources
    .map((resource) => {
      if (!resource || typeof resource !== "object" || Array.isArray(resource)) return null;
      const data = resource as Record<string, unknown>;
      const provider = renderScalar(data.provider);
      const resourceType = renderScalar(data.resource_type);
      const name = renderScalar(data.name);
      const status = renderScalar(data.status);
      if (!provider || !resourceType || !name || !status) return null;
      return [`${provider} ${resourceType}`, name, status] as [string, string, string];
    })
    .filter((row): row is [string, string, string] => Boolean(row));
}

function DeleteProjectDialog({
  project,
  workspaceUuid,
}: {
  project: BosunProjectRead;
  workspaceUuid: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState("");
  const deleteProject = useDeleteBosunProject(workspaceUuid);
  const matches = confirmation.trim() === project.slug || confirmation.trim() === project.name;
  const disabled = deleteProject.isPending || project.deletion_status === "deleting" || !matches;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!matches) return;
    try {
      await deleteProject.mutateAsync({
        projectUuid: project.uuid,
        body: { confirmation },
      });
      toast.success("Project deletion queued");
      setOpen(false);
      setConfirmation("");
    } catch (err) {
      toast.error(formatError(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="destructive"
          disabled={project.deletion_status === "deleting" || project.deletion_status === "deleted"}
        >
          <Trash2 />
          Delete project
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={(event) => void submit(event)} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Delete {project.name}</DialogTitle>
            <DialogDescription>
              This queues provider cleanup for the exact resources recorded on this project.
            </DialogDescription>
          </DialogHeader>
          <Alert tone="danger">
            <AlertDescription>
              This archives the generated GitHub repo and makes it private; it does not
              hard-delete the repo. Provider cleanup removes exact recorded Fly apps, Neon,
              Cloudflare Pages/R2/DNS resources, Slack channels, and stored Bosun project metadata
              when provider deletes succeed.
            </AlertDescription>
          </Alert>
          <div className="grid gap-2">
            <Label htmlFor="delete-confirmation">Type {project.slug} to confirm</Label>
            <Input
              id="delete-confirmation"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="off"
            />
          </div>
          {deleteProject.error ? (
            <Alert tone="danger">
              <AlertDescription>{formatError(deleteProject.error)}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" variant="destructive" disabled={disabled}>
              {deleteProject.isPending ? <Loader2 className="animate-spin" /> : <Trash2 />}
              Queue deletion
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CodexSetupForm({
  workspaceUuid,
  projectUuid,
  providerStep,
}: {
  workspaceUuid: string;
  projectUuid: string;
  providerStep: BosunProjectRead["provider_steps"][number];
}) {
  const submitCodexSetup = useSubmitBosunCodexSetup(workspaceUuid, projectUuid);
  const disabled = submitCodexSetup.isPending;
  const [copyStatus, setCopyStatus] = React.useState<{
    code: string;
    status: "copied" | "failed";
  } | null>(null);
  const copyStatusId = React.useId();
  const resources = providerStep.resources ?? {};
  const deviceAuth =
    typeof resources.device_auth === "object" && resources.device_auth !== null
      ? (resources.device_auth as Record<string, unknown>)
      : {};
  const verificationUri =
    typeof deviceAuth.verification_uri === "string"
      ? deviceAuth.verification_uri
      : undefined;
  const userCode =
    typeof deviceAuth.user_code === "string" && deviceAuth.user_code.trim()
      ? deviceAuth.user_code.trim()
      : undefined;

  if (!userCode) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-white/10 bg-black/10 p-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Waiting for generated Hermes to provide a Codex device code.
      </div>
    );
  }

  const copyDeviceCode = async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard unavailable");
      }
      await navigator.clipboard.writeText(userCode);
      setCopyStatus({ code: userCode, status: "copied" });
      toast.success("Device code copied.");
    } catch {
      setCopyStatus({ code: userCode, status: "failed" });
      toast.error("Could not copy device code.");
    }
  };
  const currentCopyStatus =
    copyStatus?.code === userCode ? copyStatus.status : "idle";

  return (
    <form
      className="grid gap-3 rounded-md border border-white/10 bg-black/10 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        submitCodexSetup.mutate(
          {},
          {
            onSuccess: (result) => {
              toast.success(result.message);
            },
            onError: (error) => {
              toast.error(formatError(error));
            },
          },
        );
      }}
    >
      {verificationUri || userCode ? (
        <div className="grid gap-2 text-sm">
          {verificationUri ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                window.open(verificationUri, "_blank", "noopener,noreferrer")
              }
            >
              <ExternalLink />
              Open Codex device auth
            </Button>
          ) : null}
          {userCode ? (
            <div className="grid gap-1">
              <Label>Device code</Label>
              <button
                type="button"
                onClick={() => void copyDeviceCode()}
                aria-label={`Copy Codex device code ${userCode}`}
                aria-describedby={copyStatusId}
                className="group flex min-h-10 w-full items-center justify-between gap-3 rounded-md border border-white/12 bg-black/20 px-3 py-2 text-left transition hover:border-white/25 hover:bg-black/30 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
              >
                <code className="min-w-0 font-mono text-base text-foreground">
                  {userCode}
                </code>
                <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-primary">
                  <Copy className="size-3.5" />
                  Copy
                </span>
              </button>
              <p
                id={copyStatusId}
                role={currentCopyStatus === "failed" ? "alert" : "status"}
                aria-live="polite"
                className={
                  currentCopyStatus === "failed"
                    ? "text-xs text-destructive"
                    : "text-xs text-muted-foreground"
                }
              >
                {currentCopyStatus === "copied"
                  ? "Device code copied."
                  : currentCopyStatus === "failed"
                    ? "Could not copy device code."
                    : "Click the device code to copy it."}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={disabled}>
          {submitCodexSetup.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            <KeyRound />
          )}
          Check Codex setup
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() =>
            window.open(
              "https://developers.openai.com/codex/auth",
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          <ExternalLink />
          Codex login guidance
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Generated Hermes runs the Codex device-auth flow and Bosun stores only
        the public URL, code, and readiness status.
      </p>
    </form>
  );
}

function SlackAppTokenForm({
  workspaceUuid,
  projectUuid,
  providerStep,
}: {
  workspaceUuid: string;
  projectUuid: string;
  providerStep: BosunProjectRead["provider_steps"][number];
}) {
  const submitToken = useSubmitBosunSlackAppToken(workspaceUuid, projectUuid);
  const [token, setToken] = React.useState("");
  const resources = providerStep.resources ?? {};
  const handoff =
    typeof resources.slack_app_token_handoff === "object" &&
    resources.slack_app_token_handoff !== null &&
    !Array.isArray(resources.slack_app_token_handoff)
      ? (resources.slack_app_token_handoff as Record<string, unknown>)
      : {};
  const instructions = Array.isArray(handoff.instructions)
    ? handoff.instructions.filter((item): item is string => typeof item === "string")
    : [];
  const slackAppId =
    typeof handoff.slack_app_id === "string" && handoff.slack_app_id.trim()
      ? handoff.slack_app_id.trim()
      : undefined;
  const settingsUrl = slackAppSettingsUrl(slackAppId);
  const disabled = submitToken.isPending || !token.trim().startsWith("xapp-");

  return (
    <form
      className="grid gap-3 rounded-md border border-white/10 bg-black/10 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        const appToken = token.trim();
        if (!appToken.startsWith("xapp-")) return;
        submitToken.mutate(
          { app_token: appToken },
          {
            onSuccess: () => {
              setToken("");
              toast.success("Slack app token validated.");
            },
            onError: (error) => {
              toast.error(formatError(error));
            },
          },
        );
      }}
    >
      <div className="grid gap-2 text-sm text-muted-foreground">
        {slackAppId ? (
          <p>
            Slack app id: <span className="font-mono text-foreground">{slackAppId}</span>
          </p>
        ) : null}
        {settingsUrl ? (
          <Button
            type="button"
            variant="outline"
            title={settingsUrl}
            onClick={() => window.open(settingsUrl, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink />
            Open Slack app settings
          </Button>
        ) : null}
        {instructions.length > 0 ? (
          <ol className="list-decimal space-y-1 pl-5">
            {instructions.map((instruction) => (
              <li key={instruction}>{instruction}</li>
            ))}
          </ol>
        ) : null}
      </div>
      <div className="grid gap-1">
        <Label htmlFor={`slack-app-token-${providerStep.key}`}>Slack app-level token</Label>
        <Input
          id={`slack-app-token-${providerStep.key}`}
          type="password"
          autoComplete="off"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          placeholder="xapp-..."
        />
      </div>
      <Button type="submit" disabled={disabled}>
        {submitToken.isPending ? <Loader2 className="animate-spin" /> : <KeyRound />}
        Validate and resume
      </Button>
      <p className="text-xs text-muted-foreground">
        Bot-token-only means Slack Web API calls can work, but Socket Mode is not ready
        until this app-level token validates. Bosun stores only encrypted project
        credentials and never shows the token again.
      </p>
    </form>
  );
}

export function ProjectProvisioningPage() {
  const { workspaceUuid, projectUuid } = Route.useParams();
  const project = useBosunProject(workspaceUuid, projectUuid);
  const workspace = useWorkspace(workspaceUuid);
  const jobUuid = project.data?.provisioning_job_uuid ?? undefined;
  const job = useJob(jobUuid);
  const deletionJobUuid = project.data?.deletion_job_uuid ?? undefined;
  const deletionJob = useJob(deletionJobUuid);
  const canDelete = canManageMembers(workspace.data?.role);
  const deletionRows = project.data ? deletionResourceRows(project.data) : [];
  const failedDeletionCount =
    typeof project.data?.deletion_status_payload?.failed_count === "number"
      ? project.data.deletion_status_payload.failed_count
      : 0;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm">
        <Link to="/projects">
          <ArrowLeft />
          Projects
        </Link>
      </Button>

      {project.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : project.error ? (
        <Alert tone="danger">
          <AlertDescription>{formatError(project.error)}</AlertDescription>
        </Alert>
      ) : project.data ? (
        <>
          <section className="rounded-lg border border-white/10 bg-white/[0.035] p-5 shadow-floating backdrop-blur-xl md:p-7">
            <PageHeader
              eyebrow="Provisioning progress"
              title={project.data.name}
              description="Bosun is creating the production stack. This page shows the operational phases only."
              actions={
                <div className="flex flex-wrap gap-2">
                  <Badge
                    tone={project.data.status === "ready" ? "success" : "info"}
                  >
                    {project.data.status}
                  </Badge>
                  {job.data ? (
                    <Badge
                      tone={toneForStatus(normalizeStatus(job.data.status))}
                    >
                      job {job.data.status}
                    </Badge>
                  ) : null}
                  {project.data.deletion_status !== "active" ? (
                    <Badge tone={project.data.deletion_status === "deleted" ? "success" : "danger"}>
                      delete {project.data.deletion_status}
                    </Badge>
                  ) : null}
                </div>
              }
            />
            {canDelete && project.data.deletion_status !== "deleted" ? (
              <div className="mt-5">
                <DeleteProjectDialog project={project.data} workspaceUuid={workspaceUuid} />
              </div>
            ) : null}
            <div className="mt-5">
              <ProjectCockpitNav
                workspaceUuid={workspaceUuid}
                projectUuid={projectUuid}
                active="timeline"
              />
            </div>
          </section>

          {job.error ? (
            <Alert tone="warning">
              <AlertDescription>
                Job status is temporarily unavailable: {formatError(job.error)}
              </AlertDescription>
            </Alert>
          ) : null}

          {deletionJob.data || project.data.deletion_status !== "active" ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="size-4 text-destructive" />
                  Cleanup status
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge
                    tone={
                      project.data.deletion_status === "deleted"
                        ? "success"
                        : project.data.deletion_status === "deleting"
                          ? "info"
                          : "danger"
                    }
                  >
                    {project.data.deletion_status}
                  </Badge>
                  {deletionJob.data ? (
                    <Badge tone={toneForStatus(normalizeStatus(deletionJob.data.status))}>
                      job {deletionJob.data.status}
                    </Badge>
                  ) : null}
                </div>
                {failedDeletionCount ? (
                  <Alert tone="danger">
                    <AlertDescription>
                      {failedDeletionCount} cleanup operation failed. Review the resource rows and
                      retry deletion after fixing provider permissions or the reported provider
                      error.
                    </AlertDescription>
                  </Alert>
                ) : null}
                <p className="text-muted-foreground">
                  Generated GitHub repositories are retained as archived/private rather than
                  hard-deleted.
                </p>
                {deletionRows.length > 0 ? (
                  <dl className="grid gap-2">
                    {deletionRows.map(([label, name, status]) => (
                      <div
                        key={`${label}-${name}`}
                        className="grid gap-1 rounded-md border border-white/10 bg-white/[0.025] p-3 sm:grid-cols-[11rem_minmax(0,1fr)_7rem]"
                      >
                        <dt className="font-medium">{label}</dt>
                        <dd className="break-words text-muted-foreground">{name}</dd>
                        <dd>{status}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="text-muted-foreground">
                    Cleanup has been queued. Resource-level results appear as the job runs.
                  </p>
                )}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Live stack progress</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3">
              {STEP_DEFS.map((step) => {
                const status = aggregateStatus(project.data, step.sourceKeys);
                const Icon = step.icon;
                const sourceSteps = project.data.provider_steps.filter(
                  (providerStep) =>
                    (step.sourceKeys as readonly string[]).includes(
                      providerStep.key,
                    ),
                );
                const phaseProgress = phaseProgressPercent(sourceSteps, status);
                const phaseEta = phaseTimingCopy(sourceSteps, status);
                return (
                  <details
                    key={step.key}
                    open={status !== "completed"}
                    className="rounded-md border border-white/10 bg-white/[0.025] p-3"
                  >
                    <summary className="flex cursor-pointer list-none items-center gap-3">
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground">
                        <Icon className="size-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{step.label}</p>
                        <p className="text-sm text-muted-foreground">
                          {statusLabel(status)}
                          {phaseEta ? ` - ${phaseEta}` : ""}
                        </p>
                        {phaseProgress !== null ? (
                          <div className="mt-2 max-w-md">
                            <ProgressBar value={phaseProgress} />
                          </div>
                        ) : null}
                      </div>
                      {iconForStatus(status)}
                      <Badge tone={toneForStatus(status)}>{statusLabel(status)}</Badge>
                    </summary>
                    <div className="mt-3 grid gap-3 border-t border-white/10 pt-3">
                      {sourceSteps.length > 0 ? (
                        sourceSteps.map((providerStep) => {
                          const facts = stepFacts(providerStep);
                          const label = sanitizeClientProvisioningText(
                            providerStep.label,
                          );
                          const providerStatus = normalizeStatus(providerStep.status, {
                            hasError: Boolean(providerStep.error),
                          });
                          const providerProgress =
                            !providerStep.is_manual &&
                            typeof providerStep.progress_percent === "number"
                              ? providerStep.progress_percent
                              : null;
                          const providerEta = timingCopy(providerStep, providerStatus);
                          const detail = renderStepText(providerStep.detail);
                          const action =
                            providerStatus === "failed"
                              ? renderStepText(providerStep.todo)
                              : null;
                          const error = renderStepText(providerStep.error);
                          const resources = providerStep.resources ?? {};
                          const slackApproval =
                            typeof resources.slack_approval === "object" &&
                            resources.slack_approval !== null &&
                            !Array.isArray(resources.slack_approval)
                              ? (resources.slack_approval as Record<string, unknown>)
                              : {};
                          const slackApprovalUrl =
                            typeof slackApproval.approval_url === "string"
                              ? slackApproval.approval_url
                              : null;
                          const url = providerStep.key !== "workspace_codex_login" &&
                            !HIDDEN_STEP_LINK_KEYS.has(providerStep.key) &&
                            (
                              providerStep.key !== "generated_hermes" ||
                              providerStatus === "failed"
                            ) &&
                            (slackApprovalUrl || providerStep.url)
                            ? sanitizeClientProvisioningText(
                                slackApprovalUrl || providerStep.url || "",
                              )
                            : null;
                          const linkLabel =
                            providerStep.key === "slack_app" && slackApprovalUrl
                              ? "Open Slack OAuth/Admin Allow"
                              : "Open link";
                          const showCodexSetup =
                            providerStep.key === "workspace_codex_login" &&
                            providerStatus === "waiting" &&
                            canDelete;
                          const showSlackAppTokenHandoff =
                            providerStep.key === "slack_app" &&
                            canDelete &&
                            (providerStatus === "waiting" || providerStatus === "failed") &&
                            typeof resources.slack_app_token_handoff === "object" &&
                            resources.slack_app_token_handoff !== null &&
                            !Array.isArray(resources.slack_app_token_handoff);
                          const showSlackAppWaiting =
                            providerStep.key === "slack_app" &&
                            !showSlackAppTokenHandoff &&
                            providerStatus === "running";
                          const deviceAuth =
                            typeof resources.device_auth === "object" && resources.device_auth !== null
                              ? (resources.device_auth as Record<string, unknown>)
                              : {};
                          const hasCodexDeviceCode =
                            typeof deviceAuth.user_code === "string" &&
                            Boolean(deviceAuth.user_code.trim());
                          return (
                            <div
                              key={providerStep.key}
                              className="grid gap-2 rounded-md bg-black/10 p-3 text-sm"
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-foreground">
                                  {label}
                                </span>
                                <Badge
                                  tone={toneForStatus(providerStatus)}
                                >
                                  {statusLabel(providerStatus)}
                                </Badge>
                                {url ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      window.open(
                                        url,
                                        "_blank",
                                        "noopener,noreferrer",
                                      )
                                    }
                                    title={url}
                                    className="inline-flex items-center gap-1 text-primary hover:underline"
                                  >
                                    {linkLabel}
                                    <ExternalLink className="size-3" />
                                  </button>
                                ) : null}
                              </div>
                              {detail ? (
                                <p className="text-muted-foreground">
                                  {detail}
                                </p>
                              ) : null}
                              {providerEta ? (
                                <div className="grid gap-1">
                                  <p className="text-xs uppercase text-muted-foreground">
                                    Timing
                                  </p>
                                  <p className="text-muted-foreground">
                                    {providerEta}
                                  </p>
                                  {providerProgress !== null ? (
                                    <ProgressBar value={providerProgress} />
                                  ) : null}
                                </div>
                              ) : null}
                              {action ? (
                                <p className="text-warning">Action: {action}</p>
                              ) : null}
                              {error ? (
                                <p className="text-destructive">
                                  Error: {error}
                                </p>
                              ) : null}
                              {showCodexSetup && hasCodexDeviceCode ? (
                                <CodexSetupForm
                                  workspaceUuid={workspaceUuid}
                                  projectUuid={projectUuid}
                                  providerStep={providerStep}
                                />
                              ) : showCodexSetup ? (
                                <div className="flex items-center gap-2 rounded-md border border-white/10 bg-black/10 p-3 text-muted-foreground">
                                  <Loader2 className="size-4 animate-spin" />
                                  Waiting for generated Hermes to provide a Codex device code.
                                </div>
                              ) : null}
                              {showSlackAppTokenHandoff ? (
                                <SlackAppTokenForm
                                  workspaceUuid={workspaceUuid}
                                  projectUuid={projectUuid}
                                  providerStep={providerStep}
                                />
                              ) : showSlackAppWaiting ? (
                                <div className="flex items-center gap-2 rounded-md border border-white/10 bg-black/10 p-3 text-muted-foreground">
                                  <Loader2 className="size-4 animate-spin" />
                                  Waiting for Bosun to create the project Slack app before
                                  operator setup is needed.
                                </div>
                              ) : null}
                              {facts.length > 0 ? (
                                <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                                  {facts.map(([label, value], index) => (
                                    <div
                                      key={`${providerStep.key}-${label}-${index}`}
                                      className="min-w-0"
                                    >
                                      <dt className="text-xs uppercase text-muted-foreground">
                                        {label}
                                      </dt>
                                      <dd className="break-words text-foreground">
                                        {value}
                                      </dd>
                                    </div>
                                  ))}
                                </dl>
                              ) : null}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Waiting for this phase to start.
                        </p>
                      )}
                    </div>
                  </details>
                );
              })}
            </CardContent>
          </Card>

          {project.data.blocked_reason ? (
            <Alert tone="warning">
              <AlertDescription>
                {sanitizeClientProvisioningText(project.data.blocked_reason)}
              </AlertDescription>
            </Alert>
          ) : null}

        </>
      ) : null}
    </div>
  );
}
